import { IconName } from '@ardoq/icons';
import { DropdownOptionType, type DropdownOption } from '@ardoq/dropdown-menu';
import {
  type ButtonConfig,
  type DropdownConfig,
  SettingsType,
} from '@ardoq/view-settings';
import type { RelationshipDiagramViewSettings } from '@ardoq/graph';
import { ViewIds } from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  relationshipDiagramCollapseAllGroups,
  relationshipDiagramExpandAllGroups,
} from './actions';
import { BooleanOrAuto } from 'tabview/types';
import { WILDCARD } from '@ardoq/common-helpers';
import { noop } from 'lodash';
import type { OnViewSettingsUpdate } from '@ardoq/data-model';
import { t, type I18nLocale } from '@ardoq/i18n';

export const expandAll = (
  { collapsedGroupIds }: RelationshipDiagramViewSettings,
  viewId: ViewIds,
  viewInstanceId: string,
  isDisabled: boolean,
  i18nLocale: I18nLocale
): ButtonConfig => ({
  id: 'expandAll',
  label: t(i18nLocale, 'appLayout:viewPaneHeader.expandAll', 'Expand all'),
  iconName: IconName.EXPAND_ALL,
  isActive: !isDisabled && collapsedGroupIds.length === 0,
  onClick: !isDisabled
    ? () =>
        dispatchAction(
          relationshipDiagramExpandAllGroups({ viewId }),
          viewInstanceId
        )
    : noop,
  type: SettingsType.BUTTON,
  isDisabled,
  popoverText: isDisabled
    ? t(
        i18nLocale,
        'appLayout:viewPaneHeader.noGroupsToExpand',
        'No groups to expand'
      )
    : undefined,
});

const allCollapsed = (collapsedGroupIds: string[]) =>
  collapsedGroupIds.some(id => id === WILDCARD);
export const collapseAll = (
  { collapsedGroupIds }: RelationshipDiagramViewSettings,
  viewId: ViewIds,
  viewInstanceId: string,
  isDisabled: boolean,
  i18nLocale: I18nLocale
): ButtonConfig => ({
  id: 'collapseAll',
  label: t(i18nLocale, 'appLayout:viewPaneHeader.collapseAll', 'Collapse all'),
  iconName: IconName.COLLAPSE_ALL,
  isActive: !isDisabled && allCollapsed(collapsedGroupIds),
  onClick: !isDisabled
    ? () =>
        dispatchAction(
          relationshipDiagramCollapseAllGroups({ viewId }),
          viewInstanceId
        )
    : noop,
  type: SettingsType.BUTTON,
  isDisabled,
  popoverText: isDisabled
    ? t(
        i18nLocale,
        'appLayout:viewPaneHeader.noGroupsToCollapse',
        'No groups to collapse'
      )
    : undefined,
});

type BooleanOrAutoDropdownArgs = Pick<
  DropdownConfig,
  'id' | 'label' | 'iconName' | 'isActive'
> & {
  currentValue: BooleanOrAuto;
  applyValue: (value: BooleanOrAuto) => void;
  optionLabels?: { auto: string; on: string; off: string };
};
export const booleanOrAutoDropdown = ({
  id,
  label,
  iconName,
  isActive,
  currentValue,
  applyValue,
  optionLabels = { auto: 'Auto', on: 'On', off: 'Off' },
}: BooleanOrAutoDropdownArgs) => ({
  id,
  label,
  iconName,
  isActive,
  type: SettingsType.DROPDOWN,
  options: [
    {
      name: 'auto',
      label: optionLabels.auto,
      isActive: currentValue === BooleanOrAuto.Auto,
      onClick: () => applyValue(BooleanOrAuto.Auto),
      type: DropdownOptionType.OPTION,
    },
    {
      name: 'on',
      label: optionLabels.on,
      isActive: currentValue === BooleanOrAuto.True,
      onClick: () => applyValue(BooleanOrAuto.True),
      type: DropdownOptionType.OPTION,
    },
    {
      name: 'off',
      label: optionLabels.off,
      isActive: currentValue === BooleanOrAuto.False,
      onClick: () => applyValue(BooleanOrAuto.False),
      type: DropdownOptionType.OPTION,
    },
  ],
});

export const SEPARATE_REFERENCES_TOGGLE = {
  id: 'separateReferences',
  label: 'Separate references',
  iconName: IconName.SEPARATE_REFERENCES,
  type: SettingsType.TOGGLE,
};

type ComponentStyleViewSettings = {
  componentStyle: 'shape' | 'icon';
};

export const componentStyleDropdown = <T extends ComponentStyleViewSettings>(
  viewId: ViewIds,
  viewSettings: T,
  onViewSettingsUpdate: OnViewSettingsUpdate,
  i18nLocale: I18nLocale
): DropdownConfig => ({
  id: 'componentStyle',
  type: SettingsType.DROPDOWN,
  label: t(
    i18nLocale,
    'appLayout:viewPaneHeader.componentStyle',
    'Component style'
  ),
  iconName: IconName.PAINT_BRUSH,
  options: [
    {
      label: t(i18nLocale, 'appLayout:viewPaneHeader.shape', 'Shape'),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.componentStyle === 'shape',
      onClick: () =>
        onViewSettingsUpdate(viewId, { componentStyle: 'shape' }, true),
    } satisfies DropdownOption,
    {
      label: t(i18nLocale, 'appLayout:viewPaneHeader.icon', 'Icon'),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.componentStyle === 'icon',
      onClick: () =>
        onViewSettingsUpdate(viewId, { componentStyle: 'icon' }, true),
    } satisfies DropdownOption,
  ],
  popoverText: t(
    i18nLocale,
    'appLayout:viewPaneHeader.componentStylePopover',
    'If a component has an image, it will always be displayed instead of the shape or icon.'
  ),
});
